import {
  Button,
  ButtonGroup,
  Table,
  TableColumn,
  TableParams,
  TableRowKey,
} from "@datapay-by-datacom/design-system";
import { useState } from "react";
import { OrgCategory, UserOrgRole } from "../../../generated/core-api";
import {
  useListOrgRolesByOrg,
  useRemoveOrgRole,
  usePermissions,
} from "../../hooks";

// Define a type that includes all properties in UserOrgRole plus our custom action keys
export type UserOrgRoleTableData = UserOrgRole & {
  actions: string;
};

interface ActionButtonsProps {
  userOrgRole: UserOrgRole;
}

function ActionButtons({ userOrgRole }: ActionButtonsProps) {
  const removeMutation = useRemoveOrgRole();

  const handleRemove = () => {
    removeMutation.mutate(userOrgRole);
  };

  return (
    <ButtonGroup>
      <Button
        variant="secondary"
        size="small"
        onPress={handleRemove}
        isDisabled={removeMutation.isPending}
      >
        {removeMutation.isPending ? "Removing..." : "Remove"}
      </Button>
    </ButtonGroup>
  );
}

// Create a function that returns the columns based on whether the user has admin permissions
const createColumns = (
  isAdmin: boolean
): TableColumn<UserOrgRoleTableData>[] => {
  const baseColumns: TableColumn<UserOrgRoleTableData>[] = [
    {
      key: "user_id",
      cell(row) {
        return row.user_id;
      },
      header() {
        return "User ID";
      },
    },
    {
      key: "user_name",
      cell(row) {
        return row.user_name || "-";
      },
      header() {
        return "Name";
      },
    },
    {
      key: "user_email",
      cell(row) {
        return row.user_email || "-";
      },
      header() {
        return "Email";
      },
    },
    {
      key: "user_mobile",
      cell(row) {
        return row.user_mobile || "-";
      },
      header() {
        return "Mobile";
      },
    },
    {
      key: "role",
      cell(row) {
        return row.role;
      },
      header() {
        return "Role";
      },
    },
  ];

  // Only add the actions column if the user is an admin
  if (isAdmin) {
    baseColumns.push({
      key: "actions",
      cell(row) {
        return <ActionButtons userOrgRole={row} />;
      },
      header() {
        return "Actions";
      },
    });
  }

  return baseColumns;
};

const rowKey: TableRowKey<UserOrgRoleTableData> = (row) =>
  `${row.org_category}__${row.org_id}__${row.user_id}`;

interface OrgUserRoleTableProps {
  category: OrgCategory;
  id: string;
}

export function OrgUserRoleTable({ category, id }: OrgUserRoleTableProps) {
  const [params, setParams] = useState<TableParams<UserOrgRoleTableData>>({});
  const { isAdminOfOrg } = usePermissions();

  // Check if user has admin permissions
  const canManageMembers = isAdminOfOrg(id);

  // Get columns based on permissions
  const columns = createColumns(canManageMembers);

  const orgMembersQuery = useListOrgRolesByOrg(category, id);

  return (
    <Table
      description="Organization Members"
      config={{
        columns,
        rowKey,
      }}
      control={{ params, onParamsChange: setParams }}
      query={orgMembersQuery}
    />
  );
}
